import React from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle, Banknote, QrCode, CreditCard } from "lucide-react";
import { RegistrationOptionsDialog } from "@/components/home/RegistrationOptionsDialog";
import { useServiceSelection } from "@/contexts/ServiceSelectionContext";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface ProductPlanSectionProps {
  title: string;
  subtitle: string;
}

const ProductPlanSection: React.FC<ProductPlanSectionProps> = ({
  title,
  subtitle
}) => {
  const {
    selectedServices,
    showError,
    showDialog,
    handleServiceSelection,
    handlePlanSelection,
    setShowDialog,
    handleRegistrationOption
  } = useServiceSelection();

  return <section id="planos" className="section-padding bg-gray-50">
      <div className="container-custom">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-drcash-maastricht-blue mb-6">
            {title}
          </h2>
          <div className="h-1 w-20 bg-gradient-to-r from-drcash-caribbean-green to-drcash-tiffany-blue rounded-full mx-auto mb-6"></div>
          <p className="text-gray-700 max-w-2xl mx-auto my-0">
            {subtitle}
          </p>
        </div>

        <div className={`mb-12 ${showError ? 'animate-shake' : ''}`}>
          <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto">
            <button onClick={() => handleServiceSelection('boleto')} className={`p-6 rounded-xl border-2 transition-all text-left hover:shadow-md ${selectedServices.boleto ? "border-drcash-caribbean-green bg-[#F5FFFA]" : showError ? "border-red-500 bg-red-50" : "border-gray-100 bg-white"}`}>
              <div className="flex items-center space-x-3 mb-3">
                <Checkbox id="boleto" checked={selectedServices.boleto} onCheckedChange={() => handleServiceSelection('boleto')} className="data-[state=checked]:bg-drcash-caribbean-green data-[state=checked]:border-drcash-caribbean-green" />
                <span className="text-lg font-bold">Boleto Parcelado</span>
              </div>
              <p className="text-sm text-gray-600 ml-7">
                Ofereça parcelamento aos seus pacientes e aumente o ticket médio
              </p>
            </button>

            <button onClick={() => handleServiceSelection('antecipacao')} className={`p-6 rounded-xl border-2 transition-all text-left hover:shadow-md ${selectedServices.antecipacao ? "border-drcash-caribbean-green bg-[#F5FFFA]" : showError ? "border-red-500 bg-red-50" : "border-gray-100 bg-white"}`}>
              <div className="flex items-center space-x-3 mb-3">
                <Checkbox id="antecipacao" checked={selectedServices.antecipacao} onCheckedChange={() => handleServiceSelection('antecipacao')} className="data-[state=checked]:bg-drcash-caribbean-green data-[state=checked]:border-drcash-caribbean-green" />
                <span className="text-lg font-bold">Antecipação de Recebíveis</span>
              </div>
              <p className="text-sm text-gray-600 ml-7">
                Receba hoje os valores das vendas no cartão sem esperar o prazo
              </p>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Annual Plan */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden relative border-2 border-drcash-tiffany-blue transform scale-105 transition-all hover:shadow-xl">
            <div className="absolute top-0 right-0 bg-drcash-tiffany-blue text-white text-xs font-bold py-1 px-3 rounded-bl-lg">
              RECOMENDADO
            </div>
            <div className="p-6 text-center border-b border-gray-100">
              <h3 className="text-2xl font-bold text-drcash-maastricht-blue mb-2">Anual</h3>
              <p className="text-gray-600 mb-2">Economize 15%</p>
              <div className="mb-4">
                <span className="text-4xl font-bold text-drcash-caribbean-green">R$127,50</span>
                <span className="text-gray-600">/mês*</span>
              </div>
              <p className="text-sm text-gray-500 mb-6">Valor à vista de R$1.530 cobrado anualmente</p>
              <div className="flex items-center justify-center gap-2 mb-4">
                <Banknote className="h-5 w-5 text-drcash-caribbean-green" />
                <QrCode className="h-5 w-5 text-drcash-caribbean-green" />
                <CreditCard className="h-5 w-5 text-drcash-caribbean-green" />
              </div>
              <button onClick={() => handlePlanSelection("annual")} className="w-full btn-primary">
                Começar agora
              </button>
            </div>
            <div className="p-6">
              <ul className="space-y-3">
                {["Acesso ao Boleto Parcelado 24x", "Acesso a Antecipação de Recebíveis de Cartão"].map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-drcash-caribbean-green mr-3 flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Semestral Plan - Updated values */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden transition-all hover:shadow-xl">
            <div className="p-6 text-center border-b border-gray-100">
              <h3 className="text-2xl font-bold text-drcash-maastricht-blue mb-2">Semestral</h3>
              <p className="text-gray-600 mb-2">Economize 5%</p>
              <div className="mb-4">
                <span className="text-4xl font-bold text-drcash-sapphire-blue">R$142,50</span>
                <span className="text-gray-600">/mês*</span>
              </div>
              <p className="text-sm text-gray-500 mb-6">Valor à vista de R$855 cobrado semestralmente</p>
              <div className="flex items-center justify-center gap-2 mb-4">
                <Banknote className="h-5 w-5 text-drcash-sapphire-blue" />
                <QrCode className="h-5 w-5 text-drcash-sapphire-blue" />
                <CreditCard className="h-5 w-5 text-drcash-sapphire-blue" />
              </div>
              <button onClick={() => handlePlanSelection("semestral")} className="w-full btn-secondary">
                Começar agora
              </button>
            </div>
            <div className="p-6">
              <ul className="space-y-3">
                {["Acesso ao Boleto Parcelado 24x", "Acesso a Antecipação de Recebíveis de Cartão"].map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-drcash-sapphire-blue mr-3 flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Monthly Plan */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden transition-all hover:shadow-xl">
            <div className="p-6 text-center border-b border-gray-100">
              <h3 className="text-2xl font-bold text-drcash-maastricht-blue mb-2">Mensal</h3>
              <p className="text-gray-600 mb-2">Mais flexibilidade</p>
              <div className="mb-4">
                <span className="text-4xl font-bold text-drcash-maastricht-blue">R$150</span>
                <span className="text-gray-600">/mês</span>
              </div>
              <p className="text-sm text-gray-500 mb-6">Valor cobrado mensalmente</p>
              <div className="flex items-center justify-center gap-2 mb-4">
                <Banknote className="h-5 w-5 text-drcash-maastricht-blue" />
                <QrCode className="h-5 w-5 text-drcash-maastricht-blue" />
                <CreditCard className="h-5 w-5 text-drcash-maastricht-blue" />
              </div>
              <button onClick={() => handlePlanSelection("monthly")} className="w-full btn-secondary">
                Começar agora
              </button>
            </div>
            <div className="p-6">
              <ul className="space-y-3">
                {["Acesso ao Boleto Parcelado 24x", "Acesso a Antecipação de Recebíveis de Cartão"].map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-drcash-maastricht-blue mr-3 flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-12 space-y-6 text-sm">
          <div className="max-w-4xl mx-auto space-y-4 text-gray-600">
            <p className="italic">
              * Os valores apresentados nos planos Semestral e Anual representam quanto o custo total do plano sairia, em média, por mês, durante o período de vigência do plano, porém esse valor é pago no total à vista.
            </p>
            <p className="italic">
              * Os valores promocionais dos planos apresentados podem sofrer alterações conforme o período e as campanhas ativas. Consulte as condições vigentes no momento da contratação.
            </p>
            <p className="italic">
              * As taxas do boleto parcelado são: Recebimento em 10 dias (padrão): 5,4%; Recebimento em 48h: 7%
            </p>
          </div>
        </div>
      </div>

      <RegistrationOptionsDialog open={showDialog} onOpenChange={() => setShowDialog(false)} onOptionSelect={handleRegistrationOption} />
    </section>;
};

export default ProductPlanSection;
